import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { getInitials } from '@heidi/utils';
import { AudioLines } from 'lucide-react';

interface RecentVisitTypeMarkerProps {
  /** The resolved patient identity for this encounter, or a falsy value when
   *  the session has no usable patient identity (see `resolvePatientName`). */
  patientName: string | null | undefined;
}

/**
 * Encounter rows replace the chat status dot with a compact type marker in
 * the same fixed leading slot: a small initials avatar for a patient-backed
 * encounter, or a small audio/Scribe icon for a session without a resolved
 * patient. Purely decorative — the row label already carries the patient or
 * session context, so both variants are `aria-hidden`.
 *
 * It carried a tooltip with the patient name and visit time until the row
 * gained its hover card (`RecentVisitHoverCardHost`), which says the same
 * thing with more room and actions attached. Two hover surfaces on one row
 * raced each other, so the marker gave its up rather than the card.
 */
export const RecentVisitTypeMarker = ({ patientName }: RecentVisitTypeMarkerProps) => {
  if (patientName) {
    return (
      <Avatar
        size="xs"
        color={getAvatarColor(patientName)}
        className="shrink-0"
        aria-hidden
        data-testid="sidebar-recent-visit-patient-avatar"
      >
        <AvatarFallback>{getInitials(patientName)}</AvatarFallback>
      </Avatar>
    );
  }

  return (
    <span
      className="flex size-4 shrink-0 items-center justify-center"
      aria-hidden
      data-testid="sidebar-recent-visit-audio-marker"
    >
      <AudioLines className="size-3 text-text-tertiary" />
    </span>
  );
};
